import { z } from 'zod'
import { mediaTypeSchema } from './media'

/**
 * One hit's full record, by id (CLI-758 follow-up). The analytics backend's `GET /v1/hit/{hit_id}`
 * returns everything readily available about a single hit — any media type, plus TVEyes broadcast
 * clips — in a shape whose key set is stable across sources (a field absent for a source is null or
 * `[]`, never omitted). apps/api reads it server-side (lib/hit-detail.ts) and maps the backend's
 * snake_case payload onto this camelCase contract, the way the stream maps the cache payloads; the
 * Library's article side panel renders it.
 *
 * This is NOT the stream row (`streamHitSchema`) — that is the cached, pre-filtered listing shape.
 * This is the by-id detail the panel fetches when a row is opened, the richer record behind it.
 */

/** A saved search this hit matched. */
export const hitQuerySchema = z.object({
  queryId: z.string().nullable(),
  queryText: z.string().nullable(),
})
export type HitQuery = z.infer<typeof hitQuerySchema>

/** One ranked passage, with the matched spans and (broadcast only) its player clip and air time. */
export const hitSnippetSchema = z.object({
  text: z.string(),
  highlights: z.array(z.string()),
  queryId: z.string().nullable(),
  queryText: z.string().nullable(),
  relevancyScore: z.number().nullable(),
  position: z.number().nullable(),
  /** Broadcast: the embeddable clip around the passage, and when it aired (ISO). Null elsewhere. */
  playerlette: z.string().nullable(),
  airedAt: z.string().nullable(),
})
export type HitSnippet = z.infer<typeof hitSnippetSchema>

/**
 * Every playable/visual asset the hit carries. `image` is the panel's backdrop; `audio` holds a
 * podcast episode or a broadcast clip's audio, `videos` a broadcast media-player URL — the panel
 * renders real `<audio>`/`<video>` players from them. A list entry is a URL string (an object with
 * a `url`/`src` is flattened to its URL by the reader).
 */
export const hitMediaSchema = z.object({
  image: z.string().nullable(),
  images: z.array(z.string()),
  videos: z.array(z.string()),
  audio: z.array(z.string()),
  logo: z.string().nullable(),
  /** Broadcast only: a link to the full clip transcript. Null elsewhere. */
  transcriptUrl: z.string().nullable(),
})
export type HitMedia = z.infer<typeof hitMediaSchema>

/**
 * One timestamped passage of a processed transcript (broadcast / podcast), as the backend's
 * `process_transcript` emits it: the text between delimiters, with its start/end as `m:ss` strings.
 * Only present with `content=true`, and only for sources that have a transcript — `[]` otherwise.
 */
export const transcriptSegmentSchema = z.object({
  text: z.string(),
  tStart: z.string().nullable(),
  tEnd: z.string().nullable(),
})
export type TranscriptSegment = z.infer<typeof transcriptSegmentSchema>

/**
 * Social engagement counters — the keys vary by platform (`likesCount`, `viewsCount`, `followers`,
 * `repostsCount`, …), so this is an open map of number-valued counters rather than a fixed set, and
 * null for a hit with no social engagement at all. The panel renders the ones it knows and lists
 * the rest.
 */
export const hitEngagementSchema = z.record(z.string(), z.number().nullable()).nullable()
export type HitEngagement = z.infer<typeof hitEngagementSchema>

/** A machine translation of the title and matched snippet. */
export const hitTranslationSchema = z.object({
  targetLang: z.string().nullable(),
  sourceLang: z.string().nullable(),
  title: z.string().nullable(),
  snippet: z.string().nullable(),
})
export type HitTranslation = z.infer<typeof hitTranslationSchema>

/**
 * TVEyes broadcast clip detail. The station/program/view-data objects are passed through as the
 * backend sends them (loose), since they are display-only here and their exact shape is the
 * backend's to own; the panel reads a handful of well-known keys defensively.
 */
export const hitBroadcastSchema = z
  .object({
    mediaType: z.string().nullable(),
    station: z.record(z.string(), z.unknown()).nullable(),
    program: z.record(z.string(), z.unknown()).nullable(),
    viewData: z.record(z.string(), z.unknown()).nullable(),
  })
  .nullable()
export type HitBroadcast = z.infer<typeof hitBroadcastSchema>

/**
 * The by-id detail contract. Every source yields the same keys; source-specific detail lands in
 * `engagement` (social), `broadcast` (TVEyes), `media`, `snippets` and `translations`. `content`
 * (full text / transcript) is present only when the panel asks for it (`content=true`), null
 * otherwise — it is the heavy half of the payload.
 */
export const hitDetailSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  /** The backend's bucket (NEWS / SOCIAL / PODCAST / ACADEMIC / BROADCAST / NEWSLETTER) in the
   *  stream's vocabulary, through `mediaTypeOfArticleType`; null when it sent none we know. The
   *  full-text licence decides on it (CLI-903), so it is never the raw string. */
  mediaType: mediaTypeSchema.nullable(),
  /** The raw underlying `hits.type`. */
  type: z.string().nullable(),
  link: z.string().nullable(),
  hostedLink: z.string().nullable(),
  outlet: z.string().nullable(),
  domain: z.string().nullable(),
  author: z.string().nullable(),
  publishDate: z.string().nullable(),
  createdAt: z.string().nullable(),
  language: z.string().nullable(),
  country: z.string().nullable(),
  reach: z.number().nullable(),
  dataSource: z.string().nullable(),
  summary: z.string().nullable(),
  description: z.string().nullable(),
  isOpinion: z.boolean().nullable(),
  isOriginal: z.boolean().nullable(),
  isBlocklisted: z.boolean().nullable(),
  syndicationGroupId: z.string().nullable(),
  queries: z.array(hitQuerySchema),
  snippets: z.array(hitSnippetSchema),
  media: hitMediaSchema,
  engagement: hitEngagementSchema,
  translations: z.array(hitTranslationSchema),
  /** Full article body as plain text — only with `content=true`. Null for a source whose heavy
   *  text is a timestamped transcript instead (see `transcript`). */
  content: z.string().nullable(),
  /** Broadcast / podcast transcript as timestamped segments — only with `content=true`, `[]` for a
   *  source that has none. A long transcript is many segments; the panel renders them scrollably. */
  transcript: z.array(transcriptSegmentSchema),
  broadcast: hitBroadcastSchema,
})
export type HitDetail = z.infer<typeof hitDetailSchema>

/** `GET /api/streams/:subaccountId/hits/:hitId` query: ask for the heavy body only when needed. */
export const hitDetailQuerySchema = z.object({
  content: z.stringbool().default(false),
})
export type HitDetailQuery = z.infer<typeof hitDetailQuerySchema>

/**
 * The analytics backend's per-hit sentiment analysis (`GET /v1/hit_analytics/{id}`), fetched in
 * parallel with the detail and shown next to the stream's own sentiment label. `score` is the
 * signed sentiment score the analyzer assigned (negative = negative, positive = positive); `label`
 * is its own word for it, when it gives one. Both nullable: a hit the analyzer has not scored, or a
 * backend that does not answer, simply shows no score — never an error on the panel.
 */
export const hitAnalyticsSchema = z.object({
  score: z.number().nullable(),
  label: z.string().nullable(),
})
export type HitAnalytics = z.infer<typeof hitAnalyticsSchema>

/** An empty engagement/media baseline, for a hit whose detail could not be fully read. */
export const EMPTY_HIT_MEDIA: HitMedia = {
  image: null,
  images: [],
  videos: [],
  audio: [],
  logo: null,
  transcriptUrl: null,
}
